import { RealtimeClient } from "./RealtimeClient";
import { RestClient } from "./restClient";

export class CameraService {
	constructor(options) {
		this._options = options;
		this._rc = new RealtimeClient();
		this._restClient = new RestClient();
	}

	getById(cameraId, callback) {
		const url = `/ecosystem/api/cameras/${cameraId}`;
		this._restClient.exec_get(url, callback);
	}

	create(camera, callback) {
		const url = "/ecosystem/api/cameras";
		const body = JSON.stringify({ camera });
		this._restClient.exec_post(url, body, callback);
	}

	update(cameraId, entityData, updateEntityData, callback) {
		const url = `/ecosystem/api/cameras/${cameraId}`;
		const body = JSON.stringify({ camera: { entityData }, updateEntityData });
		this._restClient.exec_put(url, body, callback);
	}

	delete(cameraId, callback) {
		const url = `/ecosystem/api/cameras/${cameraId}`;
		this._restClient.exec_delete(url, callback);
	}

	// share(cameraId, callback) {
	// 	const url = `/ecosystem/api/cameras/${cameraId}/share`;
	// 	this._restClient.exec_put(url, "{}", callback);
	// }

	// unshareEntity(cameraId, callback) {
	// 	const url = `/ecosystem/api/cameras/${cameraId}/unshare`;
	// 	this._restClient.exec_put(url, "{}", callback);
	// }

	getFOV(cameraId, callback) {
		const url = `/ecosystem/api/cameras/${cameraId}/fov`;
		this._restClient.exec_get(url, callback);
	}

	addFOV(cameraId, shape, callback) {
		const url = `/ecosystem/api/cameras/${cameraId}/fov`;
		const body = JSON.stringify(shape);
		this._restClient.exec_post(url, body, callback);
	}

	setFOV(cameraId, shapeId, callback) {
		const url = `/ecosystem/api/cameras/${cameraId}/fov/${shapeId}`;
		this._restClient.exec_put(url, "{}", callback);
	}

	removeFOV(cameraId, callback) {
		const url = `/ecosystem/api/cameras/${cameraId}/fov`;
		this._restClient.exec_delete(url, callback);
	}

	comment(cameraId, comment, callback) {
		const url = `/ecosystem/api/cameras/${cameraId}/comment`;
		const body = JSON.stringify({ comment: comment });
		this._restClient.exec_post(url, body, callback);
	}

	getMyCameras(callback) {
		const url = "/ecosystem/api/cameras";
		this._restClient.exec_get(url, callback);
	}

	/**
	 * getCamerasInRangeOfGeo - return a static list of cameras in range of target geometry.
	 * @param data
	 */
	getCamerasInRangeOfGeo(data, callback) {
		const url = "/ecosystem/api/cameras/inRangeOfGeo";
		const body = JSON.stringify({ ...data });
		this._restClient.exec_post(url, body, callback);
	}

	/**
	 * getPosition - return ptz coordinates of specified camera
	 * @param cameraId
	 */
	getPosition(cameraId, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/position`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * getPosition - return ptz coordinates of specified camera's
	 * @param cameraId
	 */
	getPositions(cameraIds, callback) {
		const url = `/cameras-app/api/cameras/positions/${cameraIds}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * getSnapshot - return base64 encoded jpeg of most recent live camera image
	 * returns { Data: "base64 encoded jpeg", "Time": "iso timestamp of captured image" }
	 * @param cameraId
	 */
	getSnapshot(cameraId, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/snapshot`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * setupPlaybackStream - return base64 encoded jpeg of camera at the given time
	 * returns { Data: "base64 encoded jpeg", "Time": "iso timestamp of captured image" }
	 * @param cameraId
	 * @param time time of playback as ISO timestamp
	 */
	setupPlaybackStream(cameraId, videoProfile, time, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/playback/setupStream`;
		const body = JSON.stringify({ videoProfile, time });
		this._restClient.exec_post(url, body, callback);
	}

	/**
	 * startPlaybackStream - return base64 encoded jpeg of camera at the given time
	 * returns { Data: "base64 encoded jpeg", "Time": "iso timestamp of captured image" }
	 * @param cameraId
	 * @param time time of playback as ISO timestamp
	 */
	startPlaybackStream(cameraId, videoId, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/playback/startStream/${videoId}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * startPlaybackStream - return base64 encoded jpeg of camera at the given time
	 * returns { Data: "base64 encoded jpeg", "Time": "iso timestamp of captured image" }
	 * @param cameraId
	 * @param time time of playback as ISO timestamp
	 */
	pausePlaybackStream(cameraId, videoId, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/playback/pauseStream/${videoId}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * getPlaybackStreamSnapshot - return base64 encoded jpeg of camera at the given time
	 * returns { Data: "base64 encoded jpeg", "Time": "iso timestamp of captured image" }
	 * @param cameraId
	 * @param time time of playback as ISO timestamp
	 */
	getPlaybackStreamSnapshot(cameraId, videoId, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/playback/snapshot/${videoId}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * streamPlayback - return continuous stream of objects containing base64 encoded jpeg of most playback given videoId
	 *   camera image and timestamp in format:
	 *   { Data: "base64 encoded jpeg", "Time": "iso timestamp of captured image" }
	 * @param cameraId
	 * @param videoId
	 * @param videoProfile (desktop, mobile) if not included will use default profile
	 *   video profile affects the size and quality of image as well as frame rate and can be unique for each camera
	 */
	streamPlayback(cameraId, videoId, callback) {
		const args = { cameraId, videoId };
		// -- returns promise that will resolve to channel that can be used for unsubscribe
		return this._rc.subscribe("cameras-app", "/playbackStream", args, function (msg) {
			const response = JSON.parse(msg);
			if (response) {
				if (callback) {
					callback(null, response);
				}
			}
		});
	}

	getByDisplayTargetId(displayTargetId, callback) {
		const url = `/ecosystem/api/cameras/getByDisplayTargetId/${displayTargetId}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * getPresets - get available presets for camera
	 * @param cameraId
	 */
	getPresets(cameraId, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/presets`;
		if (callback) {
			this._restClient.exec_get(url, callback);
		} else {
			return new Promise((resolve, reject) => {
				this._restClient.exec_get(url, (err, result) => {
					if (err) reject(err);
					resolve(result);
				});
			});
		}
	}

	/**
	 * gotoPreset - command to move camera to pre-configured preset ptz coords
	 * @param cameraId
	 * @param preset
	 */
	gotoPreset(cameraId, preset, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/presets/${preset}/goto`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * savePreset - save current camera position to a preset
	 * @param cameraId unique id of camera
	 * @param presetNumber - preset number to save current position to
	 */
	savePreset(cameraId, presetNumber, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/presets`;
		const body = JSON.stringify({ presetNumber: presetNumber });
		this._restClient.exec_put(url, body, callback);
	}

	/**
	 * sendAuxCmd - command to move camera to pre-configured preset ptz coords
	 * @param cameraId
	 * @param cmd
	 */
	sendAuxCmd(cameraId, cmd, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/sendAuxCmd?cmd=${cmd}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * moveHome - move camera to its preprogrammed home ptz coordinates
	 * @param cameraId
	 */
	moveHome(cameraId, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/move/home`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * moveContinuous - move camera on specified axis, in specified direction at specified speed
	 * @param {string} cameraId - Unique ID of the camera to move
	 * @param {string|null} instanceId - Optional instance ID for virtual cameras
	 * @param {number} pan - Pan speed/direction (-1 to 1)
	 * @param {number} tilt - Tilt speed/direction (-1 to 1)
	 * @param {number} zoom - Zoom speed/direction (-1 to 1)
	 * @param {function} callback - Callback function to handle response
	 */
	moveContinuous(cameraId, instanceId = null, pan = 0, tilt = 0, zoom = 0, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/move/continuous${instanceId ? "?instanceId=" + instanceId : ""}`;
		const movement = { pan, tilt, zoom };
		const body = JSON.stringify(movement);
		this._restClient.exec_post(url, body, callback);
	}

	/**
	 * moveToPoint - move camera to a specified position
	 * @param cameraId unique id of camera to move
	 * @param width width of video control in px
	 * @param height height of video control in px
	 * @param x value of point clicked on x axis in px
	 * @param y value of point clicked on y axis in px
	 *
	 */
	moveToPoint(cameraId, width, height, x, y, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/move/toPoint?width=${width}&height=${height}&x=${x}&y=${y}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * moveAbsolute - move camera to a specified pan/tilt position
	 * @param cameraId unique id of camera to move
	 * @param pan 0 to 360
	 * @param tilt -180 to 180
	 * @param speed  0 to 1
	 */
	moveAbsolute(cameraId, pan, tilt, speed, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/move/absolute?pan=${pan}&tilt=${tilt}&spd=${speed}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * zoomAbsolute - zoom camera to a specified position
	 * @param cameraId unique id of camera to zoom
	 * @param zoom 0 (zoomed fully out) to 1 (zoomed fully in)
	 * @param speed 0 to 1
	 */
	zoomAbsolute(cameraId, zoom, speed, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/zoom/absolute?zoom=${zoom}&spd=${speed}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * focusContinuous - move camera focus continuously while input is held
	 * @param cameraId unique id of camera to focus
	 * @param direction -1 for near, 1 for far, 0 to stop
	 */
	focusContinuous(cameraId, direction, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/move/continuous`;
		const movement = { focus: direction };
		const body = JSON.stringify(movement);
		this._restClient.exec_post(url, body, callback);
	}

	/**
	 * irisContinuous - move camera iris continuously while input is held
	 * @param cameraId unique id of camera to control iris
	 * @param direction -1 to close, 1 to open, 0 to stop
	 */
	irisContinuous(cameraId, direction, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/move/continuous`;
		const movement = { iris: direction };
		const body = JSON.stringify(movement);
		this._restClient.exec_post(url, body, callback);
	}

	/**
	 * getFocus - get the current focus settings of the specified camera
	 * @param cameraId unique id of camera to get focus settings
	 * @param callback callback function to handle response
	 */
	getFocus(cameraId, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/focus`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * getIris - get the current iris settings of the specified camera
	 * @param cameraId unique id of camera to get iris settings
	 * @param callback callback function to handle response
	 */
	getIris(cameraId, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/iris`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * moveRelative - move camera a distance relative to its current position (nudge)
	 * @param cameraId unique id of camera to move
	 * @param pan direction of pan -1 (ccw) 1 (cw)
	 * @param tilt direction of tilt -1 (down) 1 (up)
	 * @param distance distance to move between 0 and 1 inclusive
	 * @param speed 0 to 1
	 */
	moveRelative(cameraId, pan, tilt, distance, speed, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/move/relative?pan=${pan}&tilt=${tilt}&distance=${distance}&spd=${speed}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * zoomRelative - zoom camera a distance relative to its current zoom level (nudge)
	 * @param cameraId unique id of camera to zoom
	 * @param zoom direction of zoom -1 (out) 1 (in)
	 * @param distance distance to zoom between 0 and 1 inclusive
	 * @param speed 0 to 1
	 */
	zoomRelative(cameraId, instanceId = null, zoom, distance, speed, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/zoom/relative?zoom=${zoom}&distance=${distance}&spd=${speed}${
			instanceId ? "&instanceId=" + instanceId : ""
		}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * slew camera to a lat/lon/alt coordinate
	 * @param cameraId unique id of camera to slew
	 * @param lat latitude of location to slew to
	 * @param lon longitude of target to slew to
	 * @param alt altitude of location to slew to
	 * @param speed 0 to 1
	 */
	moveGeo(cameraId, lat, lon, alt, speed, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/move/geo?lat=${lat}&lon=${lon}&alt=${alt}&spd=${speed}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * set slewLock property of camera to target
	 * @param cameraId unique id of camera to slew
	 * @param targetId id of target being slewed to
	 * @param targetType type of target being slewed to
	 * @param initialGeo geometry of target at time method is called
	 */
	setSlewLock(cameraId, targetId, targetType, initialGeo, callback) {
		const url = `/ecosystem/api/cameras/${cameraId}/setSlewLock`;
		const body = JSON.stringify({
			id: targetId,
			type: targetType,
			initialGeo
		});
		this._restClient.exec_put(url, body, callback);
	}

	/**
	 * set slewLock property of camera to null
	 * @param cameraId unique id of camera to slew
	 */
	releaseSlewLock(cameraId, callback) {
		const url = `/ecosystem/api/cameras/${cameraId}/releaseSlewLock`;
		this._restClient.exec_put(url, null, callback);
	}

	/**
	 * streamVideo - return continuous stream of objects containing base64 encoded jpeg of most recent live
	 *   camera image and timestamp in format:
	 *   { Data: "base64 encoded jpeg", "Time": "iso timestamp of captured image" }
	 * @param cameraId
	 * @param videoProfile (desktop, mobile) if not included will use default profile
	 *   video profile affects the size and quality of image as well as frame rate and can be unique for each camera
	 */
	streamVideo(cameraId, videoProfile, options = {}, callback) {
		const args = {
			cameraId: cameraId,
			videoProfile: videoProfile,
			options: options
		};
		// -- returns promise that will resolve to channel that can be used for unsubscribe
		return this._rc.subscribe(
			"cameras-app",
			`/${options.api || "liveStream"}`,
			args,
			function (msg) {
				const response = typeof msg === "string" ? JSON.parse(msg) : msg;
				if (response) {
					if (callback) {
						callback(null, response);
					}
				}
			},
			null,
			false
		);
	}

	/**
	 * changeStream - control a replay video stream, speed, jump to a particular time and change player dimensions
	 * @param cameraId unique id of camera to zoom
	 * @param videoStreamId uniqueId of a currently active videoStream
	 * @param changes object containing properties to change on the video stream
	 *   Options include spd:(number), time:(date), playerWidth:(number), playerHeight:(number)
	 */
	changeStream(cameraId, videoStreamId, changes, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/videoStream/${videoStreamId}`;
		const body = JSON.stringify(changes);
		this._restClient.exec_put(url, body, callback);
	}

	/**
	 * virtualCommand: issue a command to a virtual instance of a camera
	 * @param cameraId unique id of camera to issue command for
	 * @param instanceData
	 * @param command
	 */
	virtualCommand(cameraId, instanceData, command, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/virtualCommand`;
		const body = {
			cameraInstance: instanceData,
			command: command
		};
		this._restClient.exec_post(url, JSON.stringify(body), callback);
	}

	streamFOVs(cameraIds, callback) {
		const args = {
			cameraIds: cameraIds
		};

		return this._rc.subscribe("ecosystem", "/fovs", args, msg => {
			const response = JSON.parse(msg);
			if (response) {
				if (callback) callback(null, response);
			}
		});
	}

	/**
	 * Get endpoint for video stream
	 * @param {string} cameraId
	 */
	getStreamURL(cameraId, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/streamUrl`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * Get endpoint for playback stream URL
	 * @param {string} cameraId
	 * @param {string} start - Start datetime for playback
	 * @param {string} end - End datetime for playback
	 * @param {function} callback - Callback function to handle response
	 */
	getPlaybackURL(cameraId, start, end, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/playbackStreamUrl?start=${encodeURIComponent(
			start
		)}&end=${encodeURIComponent(end)}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * Create or Update the spotlightShape property on a camera
	 * @param {string} cameraId
	 * @param {object} data - GeoJson
	 * @param {function} callback
	 */
	upsertSpotlightShape(cameraId, data, callback) {
		const body = JSON.stringify({ data: data });
		const url = `/ecosystem/api/cameras/${cameraId}/spotlight`;
		this._restClient.exec_put(url, body, callback);
	}

	/**
	 * Remove the spotlightShape property from a camera
	 * @param {string} cameraId
	 * @param {function} callback
	 */
	deleteSpotlightShape(cameraId, callback) {
		const url = `/ecosystem/api/cameras/${cameraId}/spotlight`;
		this._restClient.exec_delete(url, callback);
	}

	/**
	 * Remove a camera from its display target
	 * @param {string} cameraId
	 * * @param {string} displayTargetId
	 * @param {function} callback
	 */
	removeCameraFromDisplayTarget(cameraId, displayTargetId, callback) {
		const url = `/ecosystem/api/cameras/${cameraId}/displayTarget/${displayTargetId}`;
		this._restClient.exec_put(url, callback);
	}

	/**
	 * Get all cameras a user is able to link on a given entity
	 * @param {function} callback
	 */
	getCamerasForLinking(entityId, entityType, query, pageSize, callback) {
		const url = `/ecosystem/api/cameras/getCamerasForLinking/${entityId}/linkable?entityType=${entityType}&query=${query}&pageSize=${pageSize}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * Adds a new stream to the wowza streaming engine; if a stream is already running, it will simply connect and return the connection data.
	 * @param  cameraId
	 * @param isForPlayback
	 * @param start
	 * @param end
	 * @param callback
	 */
	getWowzaStream(cameraId, isForPlayback = false, start = "", end = "", callback) {
		const url = "/cameras-app/api/cameras/wowzaWebRtc";
		const body = JSON.stringify({ cameraId, isForPlayback, start, end });
		this._restClient.exec_post(url, body, callback);
	}

	/**
	 *
	 * @param cameraId
	 * @param playback
	 * @param callback
	 */
	removeWowzaStream(cameraId, isForPlayback, callback) {
		const url = `/cameras-app/api/cameras/removeWowzaStream`;
		const body = JSON.stringify({ cameraId, isForPlayback });
		this._restClient.exec_post(url, body, callback);
	}

	/**
	 * getPlayerSession
	 * This method is used to fetch the session token, which can be used to authenticate the camera stream and websocket connection.
	 * @param  cameraId
	 * @param  body optional body object, e.g. { refresh_token } to refresh an existing session
	 * @param callback
	 */
	getPlayerSession(cameraId, body, callback) {
		const url = `/cameras-app/api/cameras/${cameraId}/playerSession`;
		this._restClient.exec_post(url, JSON.stringify(body || {}), callback);
	}

	generateDeviceManagerPDF(data, callback) {
		const url = `/cameras-app/api/deviceManager/genarate-pdf`;
		const body = JSON.stringify(data);

		this._restClient.exec_post(url, body, callback);
	}

	/**
	 * Explicit PTZ lock for a camera
	 * POST /ecosystem/api/cameras/:cameraId/lock
	 * Identity (userId, orgId) is taken from the auth token on the server.
	 */
	setExplicitLock(cameraId, callback) {
		const url = `/ecosystem/api/cameras/${cameraId}/lock`;
		// No request body required – send empty JSON object
		this._restClient.exec_post(url, "{}", callback);
	}

	/**
	 * Explicit PTZ unlock for a camera
	 * POST /ecosystem/api/cameras/:cameraId/unlock
	 */
	clearExplicitLock(cameraId, callback) {
		const url = `/ecosystem/api/cameras/${cameraId}/unlock`;
		this._restClient.exec_post(url, "{}", callback);
	}

	/**
	 * Temporary PTZ lock for a camera
	 * POST /ecosystem/api/cameras/:cameraId/tempLock
	 */
	setTemporaryLock(cameraId, callback) {
		const url = `/ecosystem/api/cameras/${cameraId}/tempLock`;
		this._restClient.exec_post(url, "{}", callback);
	}
	/**
	 * streamPtzPrivilege
	 * Realtime PTZ privilege snapshot stream for a camera.
	 * Uses /ecosystem realtime /ptzPrivilege pubsub.
	 *
	 * @param {string} cameraId
	 * @param {function} callback  (err, snapshot) => void
	 * @returns {Promise<any>} subscription handle (for unsubscribe)
	 */
	streamPtzPrivilege(cameraId, callback) {
		const args = { cameraId };

		// Returns a promise that resolves to a channel you can use to unsubscribe
		return this._rc.subscribe("ecosystem", "/ptzPrivilege", args, msg => {
			const response = typeof msg === "string" ? JSON.parse(msg) : msg;
			if (response && callback) {
				callback(null, response);
			}
		});
	}
}
